import { create } from 'zustand';
import dayjs, { Dayjs } from 'dayjs';
import { BUILD_INFO } from '@libs/config';

type State = {
  builtVersion: string;
  foundBuildVersion: string;
  builtTime: Dayjs;
  foundBuildTime: Dayjs;
  isNewVersionAvailable: boolean;
  checkBuildTime: () => Promise<BuildInfo | null>;
  refreshApp: () => void;
};

export const useNewVersionCheckStore = create<State>()((set, get) => ({
  builtTime: dayjs(BUILD_INFO.BUILD_DATE_TIME || Date.now()),
  builtVersion: BUILD_INFO.APP_VERSION,
  foundBuildTime: dayjs(BUILD_INFO.BUILD_DATE_TIME || Date.now()),
  foundBuildVersion: BUILD_INFO.APP_VERSION,
  isNewVersionAvailable: false,

  checkBuildTime: async () => {
    const { builtTime, builtVersion } = get();
    if (BUILD_INFO.BUILD_MODE === 'development' && BUILD_INFO.START_MODE === 'local') {
      return null; // do not check for new version in development mode
    }
    try {
      const buildInfo = await fetchBuildInfoJson('buildInfo.json');
      if (dayjs(buildInfo.buildTime).isAfter(builtTime) || buildInfo.version !== builtVersion) {
        // update store only in case if the build time or version is different
        set({
          foundBuildTime: dayjs(buildInfo.buildTime),
          foundBuildVersion: buildInfo.version,
          isNewVersionAvailable:
            dayjs(buildInfo.buildTime).isAfter(get().builtTime) || buildInfo.version !== builtVersion,
        });
      }
      return buildInfo;
    } catch (error) {
      console.error('Could not load build info');
      return null;
    }
  },
  refreshApp: () => {
    window.location.reload();
  },
}));

const fetchBuildInfoJson = async (filename: string): Promise<BuildInfo> => {
  try {
    const url = new URL(filename, window.location.origin);
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filename}: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching ${filename}:`, error);
    throw error;
  }
};

type BuildInfo = {
  buildTime: number;
  version: string;
};
